import { GET_ALL_ROUTES_BY_DATE_URL, GET_BUS_OPERATORS_URL, GET_SCHEDULE_UPDATE_LOGS_BY_ID_URL } from "../utils/api-url";
import { AGENT_SERVER_URL_LIST, API_SRMS_BASE_URL, API_NORTH_BASE_URL, API_B2B_BASE_URL } from "../app.config";
import { message } from "antd";
import { postCall } from "../utils/network.util";
import { isValid } from "../utils/utilities";

// Resolves the single server to query for the selected operator. Used only when there
// are multiple agent servers (prod). SRMS -> srms server, north region -> north server,
// everything else -> b2b server.
const getServerUrlForOperator = (operatorCode, region) => {
    if (operatorCode === 'SRMS') {
        return API_SRMS_BASE_URL;
    }
    if (isValid(region) && region.toLowerCase() === 'north') {
        return API_NORTH_BASE_URL;
    }
    return API_B2B_BASE_URL;
}

// When operatorCode is not provided the search returns routes for all operators in the date range.
export const fetchAllRoutesByDate = (selectedDate, onSuccess = () => {}, onFailure = () => {}, operatorCode = null, region = null) => {
    let agentServerUrl = AGENT_SERVER_URL_LIST;

    // If no agent servers, call onFailure
    if (!agentServerUrl || agentServerUrl.length === 0) {
        onFailure();
        return;
    }

    // When an operator is selected and there are multiple servers, only query the
    // server that hosts that operator instead of fanning out to all servers.
    if (agentServerUrl.length > 1 && isValid(operatorCode)) {
        agentServerUrl = [getServerUrlForOperator(operatorCode, region)];
    }

    // Create an array of fetch promises for all servers
    const fetchPromises = agentServerUrl.map(serverUrl => {
        const fullUrl = `${serverUrl}${GET_ALL_ROUTES_BY_DATE_URL}`;
        return fetch(fullUrl, {
            method: "POST",
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                inputDate: selectedDate,
                isOTAs: true,
                "operatorCode": operatorCode,
                "travelsId": global.travelsId
            })
        })
        .then(response => response.json())
        .then(data => {
            if (data.Status === 'SUCCESS') {
                return data.Payload || [];
            }
            return [];
        })
        .catch((reason) => {
            console.error(`Fetching schedules failed for server ${serverUrl}: ${reason}`);
            return [];
        });
    });

    // Execute all promises in parallel using Promise.all
    Promise.all(fetchPromises)
    .then(results => {
        // Concatenate all results
        const allResults = results.reduce((acc, result) => {
            if (Array.isArray(result)) {
                return acc.concat(result);
            } else if (result) {
                return acc.concat([result]);
            }
            return acc;
        }, []);

        if (allResults.length > 0) {
            onSuccess(allResults);
        } else {
            onFailure();
            if (isValid(operatorCode)) {
                message.error("No route for the operator in the given date range")
            }
            else {
                message.error(`Fetching schedules failed for all servers`);
            }
        }
    })
    .catch((reason) => {
        console.error(`Promise.all failed: ${reason}`);
        onFailure();
        message.error(`Fetching schedules failed`);
    });
}

// Treats MySQL tinyint (1/0), boolean and "1"/"true" as enabled.
const isMavenEnabled = (operator) => {
    const value = operator.show_inventory_for_maven !== undefined
        ? operator.show_inventory_for_maven
        : operator.show_maven;
    return value === 1 || value === true || value === '1' || value === 'true';
}

// Fetches active operators from the b2b admin server only (north/srms not required)
// and returns only the operators enabled for maven (show_maven).
export const fetchBusCompanyList = (onSuccess = () => {}, onFailure = () => {}) => {
    postCall(GET_BUS_OPERATORS_URL, {})
        .then((data) => {
            if (data.status === "SUCCESS" && data.payload && data.payload.Status === "SUCCESS") {
                const travelsInfo = (data.payload.Payload && data.payload.Payload.travelsInfo) || [];
                const operators = travelsInfo
                    .filter(isMavenEnabled)
                    .map(operator => ({
                        operatorCode: operator.operator_code,
                        operatorName: operator.name,
                        operatorId: operator.id,
                        region: operator.region,
                    }));

                if (operators.length > 0) {
                    onSuccess(operators);
                } else {
                    onFailure();
                    message.error(`No operators found`);
                }
            } else {
                onFailure();
                message.error(`Fetching operators failed`);
            }
        })
        .catch((reason) => {
            console.error(`Fetching operators failed: ${JSON.stringify(reason)}`);
            onFailure();
            message.error(`Fetching operators failed`);
        });
}

export const getScheduleUpdateLogsById = (scheduleId, onSuccess = () => {}, onFailure = () => {}) => {
  let fetched = postCall(GET_SCHEDULE_UPDATE_LOGS_BY_ID_URL, {
    scheduleId: scheduleId
  });
  fetched.then((data) => {
    if(data.status == "SUCCESS" && data.payload.Status == "SUCCESS"){
      onSuccess(data.payload.Payload);
    }else{
      onFailure("Unable to get schedule update logs.");
    }
  })
  .catch((reason) => {
    console.error({reason});
    onFailure("Failed to get schedule update logs!");
  })
}